/**
 * Web route keys for the Airtable `App_Messages` framework (ENVDB-7836).
 *
 * A route key is the React Router path pattern with `:section` substituted and every other
 * parameter left literal — `/consignments/edit/:id/description`, `/consignments/summary/:id`.
 * `:id` deliberately stays literal: substituting it would produce a per-consignment key that no
 * Airtable option could ever match.
 *
 * Every `pattern` must match a `ProtectedRoute`/`AdminProtectedRoute` path in
 * `src/common/containers/AppNavigator/router.tsx` — `app-message-routes.test.ts` asserts that in
 * both directions, so a route added to the router without an entry here fails the build.
 *
 * `sections` expands one pattern into one key per wizard step.
 */

export interface IAppMessageRoute {
    /** React Router path pattern, exactly as declared in `router.tsx`. */
    pattern: string;
    /** Wizard sections this pattern expands over; one key per section. */
    sections?: string[];
}

/** The consignment wizard's steps, in tab order. `additional` is excluded: it is never mounted. */
const CONSIGNMENT_SECTIONS = [
    'movement',
    'forms',
    'description',
    'history',
    'food',
    'chemical',
    'declaration',
    'transporter',
];

/** The template wizard's steps: the same, with `template` in place of `movement`. */
const TEMPLATE_SECTIONS = ['template', ...CONSIGNMENT_SECTIONS.slice(1)];

export const APP_MESSAGE_ROUTES: IAppMessageRoute[] = [
    { pattern: '/consignments/edit/:id/:section', sections: CONSIGNMENT_SECTIONS },
    { pattern: '/templates/edit/:id/:section', sections: TEMPLATE_SECTIONS },
    { pattern: '/consignments/summary/:id' },
    { pattern: '/consignments/copy/:id' },
    { pattern: '/consignments/:id/movement' },
    { pattern: '/consignments' },
    { pattern: '/consignments/add' },
    { pattern: '/consignments/add-select' },
    { pattern: '/consignments/select-template' },
    { pattern: '/templates' },
    { pattern: '/templates/add/:id' },
    { pattern: '/profile' },
    { pattern: '/edecs' },
    { pattern: '/admin' },
    { pattern: '/register' },
];

/** The `:section` placeholder, substituted per wizard step. */
const SECTION_PARAM = ':section';

/**
 * The route key for one pattern and, for a `:section` pattern, one section. Total by design:
 * an absent or unexpected `path` yields `''`, which never matches any row, rather than throwing.
 */
export const buildWebRouteKey = (
    // `readonly` matters: react-router types `RouteProps['path']` as
    // `string | readonly Path[] | undefined`, and a readonly array is not assignable to `string[]`.
    path?: string | readonly string[] | null,
    section?: string | null
): string => {
    if (typeof path !== 'string' || path === '') {
        return '';
    }
    if (!path.includes(SECTION_PARAM)) {
        return path;
    }
    return typeof section === 'string' && section !== '' ? path.replace(SECTION_PARAM, section) : '';
};

/** Every addressable web route key, in declaration order. */
export const WEB_ROUTE_KEYS: string[] = APP_MESSAGE_ROUTES.flatMap((route) =>
    route.sections && route.sections.length > 0
        ? route.sections.map((section) => buildWebRouteKey(route.pattern, section))
        : [route.pattern]
);
